import * as React from 'react';
import * as $ from 'jquery';

import { HTMLDivRef } from '../../../../pdc-platform/src/ts/Interfaces';
import { DialogDefaultSizeType, IPosition, ISize } from '../../ts/Interfaces';
import * as reactGlobalClick from '../../ts/reactGlobalClick';

export function getStyles(
    initialContainerHeight: number,
    initialContentWidth: number,
    containerHeightOnResize: number,
    containerHeight: number,
    position: IPosition,
    narrowLayout: boolean,
    setFullHeight?: boolean,
    zIndex?: number
) {
    const modalStyle: React.CSSProperties = {
        textAlign: 'center'
    };
    const containerStyle: React.CSSProperties = {
        maxWidth: '100%'
    };
    const contentStyle: React.CSSProperties = {
        maxHeight: (screen.height / 100) * 85 + 'px'
    };
    const bodyStyle: React.CSSProperties = {};

    if (zIndex != null) {
        modalStyle.zIndex = zIndex;
    }

    // It makes form's width be not changed on changing of the content.
    if (initialContentWidth != null) {
        containerStyle.width = initialContentWidth + 'px';
    }

    if (!narrowLayout) {
        if (position != null) {
            contentStyle.left = position.left + 'px';
            contentStyle.top = position.top + 'px';
            modalStyle.textAlign = 'initial';
        }

        if (containerHeightOnResize > 0) {
            bodyStyle.height = containerHeightOnResize;
        } else if (containerHeight > 0) {
            bodyStyle.height = containerHeight;
        } else if (initialContainerHeight > 0) {
            bodyStyle.height = initialContainerHeight;
        }

        if (setFullHeight) {
            bodyStyle.height = bodyStyle.maxHeight;
        }
    }

    return {
        modalStyle,
        containerStyle,
        contentStyle,
        bodyStyle
    };
}

interface IDialogSize extends ISize<number> {
    position?: IPosition;
}

const _dialogSizes: {
    [dialogName: string]: IDialogSize;
} = {};

function getDefaultSizeValue(defaultWidgetEditSize: DialogDefaultSizeType): number {
    if (defaultWidgetEditSize == null) {
        return null;
    }

    switch (defaultWidgetEditSize) {
        case 'max':
            return 0.832;
        case '4/5':
            return 4 / 5;
        case '3/4':
            return 3 / 4;
        case '2/3':
            return 2 / 3;
        case '1/2':
            return 1 / 2;
        case '1/3':
            return 1 / 3;
        case '1/4':
            return 1 / 4;
        default:
            return null;
    }
}

function getInitialContainerHeight(size: IDialogSize, defaultContainerHeight: DialogDefaultSizeType) {
    if (size != null && size.height != null) {
        return size.height;
    } else if (typeof defaultContainerHeight === 'number') {
        return defaultContainerHeight;
    } else {
        const defaultHeight = getDefaultSizeValue(defaultContainerHeight);

        if (defaultHeight > 0) {
            return window.innerHeight * defaultHeight;
        }
    }

    return null;
}

function getInitialContentWidth(size: IDialogSize, defaultContentWidth: DialogDefaultSizeType) {
    if (size != null && size.width != null) {
        return size.width;
    } else if (typeof defaultContentWidth === 'number') {
        return defaultContentWidth;
    } else {
        const defaultWidth = getDefaultSizeValue(defaultContentWidth);

        if (defaultWidth > 0) {
            return window.innerWidth * defaultWidth;
        }
    }

    return null;
}

// 'dialogName' is specified to make the dialog restoring its size and position on the next opening.
export interface IResizeAndMoveProperties {
    dialogName?: string;
    resizable?: boolean | 'height' | 'width' | 'both';
    draggable?: boolean;
    disableOverflow?: boolean;
    closeOnWindowResize?: boolean;
}

export function useResizeAndMoveHook(
    props: IResizeAndMoveProperties,
    defaultContainerHeight: DialogDefaultSizeType,
    defaultContentWidth: DialogDefaultSizeType,
    contentRef: HTMLDivRef,
    headerRef?: HTMLDivRef,
    footerRef?: HTMLDivRef,
    minHeight = 300,
    minWidth = 300
) {
    const dialogName = props?.dialogName;
    const resizable = props?.resizable ?? true;
    const draggable = props?.draggable ?? true;
    const closeOnWindowResize = props?.closeOnWindowResize ?? false;

    let size = dialogName != null ? _dialogSizes[dialogName] : undefined;

    const initialContainerHeight = getInitialContainerHeight(size, defaultContainerHeight);
    const initialContentWidth = React.useRef<number>(getInitialContentWidth(size, defaultContentWidth));
    const [containerSize, setContainerSize] = React.useState<ISize<number>>({
        height: initialContainerHeight,
        width: initialContentWidth.current
    });
    const [position, setPosition] = React.useState<IPosition>(size?.position);
    const resizeTimer = React.useRef<NodeJS.Timer>(null);
    const resizeStarted = React.useRef(false);

    React.useEffect(() => {
        const content = contentRef.current != null ? $(contentRef.current) : null;
        const resizeIsAllowed = resizable !== false && content != null && typeof content.resizable === 'function';
        const moveIsAllowed = draggable && content != null && typeof content.draggable === 'function';

        // When the window is resized, the popup's component can change its position.
        // But the popup's position is absolute. So, it can make the popup being positioned wrong.
        // This is a trick to fix it by closing the popup on window's resize.
        const handleOnWindowResize = (e) => {
            if (resizeTimer.current != null) {
                clearTimeout(resizeTimer.current);
                resizeTimer.current = null;
            }

            if (!resizeStarted.current) {
                resizeTimer.current = setTimeout(() => {
                    reactGlobalClick.trigger(e);
                }, 250);
            }
        };

        if (resizeIsAllowed) {
            if (size == null && defaultContentWidth === 'auto') {
                initialContentWidth.current = content.width();
            }

            const getResizeHandler = (forStart: boolean) => {
                return (event, ui) => {
                    if (forStart) {
                        reactGlobalClick.setDoNotProcessFlag(true);
                        resizeStarted.current = true;
                    }

                    let newContainerHeight = content.height();

                    if (headerRef != null) {
                        newContainerHeight -= $(headerRef.current).outerHeight();
                    }

                    if (footerRef != null) {
                        newContainerHeight -= $(footerRef.current).outerHeight();
                    }

                    if (newContainerHeight < minHeight) {
                        newContainerHeight = minHeight;
                    }

                    let newContainerWidth = content.width();

                    if (newContainerWidth < minWidth) {
                        newContainerWidth = minWidth;
                    }

                    if (size == null) {
                        size = {};

                        if (dialogName != null) {
                            _dialogSizes[dialogName] = size;
                        }
                    }

                    size.height = newContainerHeight;
                    size.width = newContainerWidth;

                    setContainerSize({
                        height: newContainerHeight,
                        width: newContainerWidth
                    });
                    $('.listen-container-resize', contentRef.current).trigger('container-resize', [newContainerHeight]);
                };
            };

            const handleOnResizeStop = () => {
                resizeStarted.current = false;
                reactGlobalClick.setDoNotProcessFlag(false);
            };

            let handles = 'all';

            if (props.resizable === 'height') {
                handles = 's';
            }

            if (props.resizable === 'width') {
                handles = 'e';
            }

            content.resizable({
                start: getResizeHandler(true),
                resize: getResizeHandler(false),
                stop: handleOnResizeStop,
                handles: handles,
                minHeight: minHeight,
                minWidth: minWidth
            });
        }

        if (moveIsAllowed) {
            const handleOnDragStop = () => {
                if (size == null) {
                    size = {};

                    if (dialogName != null) {
                        _dialogSizes[dialogName] = size;
                    }
                }

                size.position = content.position();
                setPosition(size.position);
            };

            content.draggable({
                handle: '.modal-header',
                stop: handleOnDragStop
            });
        }

        if (closeOnWindowResize && !props.disableOverflow) {
            $(window).on('resize', handleOnWindowResize);
        }

        return () => {
            resizeStarted.current = false;
            reactGlobalClick.setDoNotProcessFlag(false);

            if (resizeTimer.current != null) {
                clearTimeout(resizeTimer.current);
                resizeTimer.current = null;
            }

            if (closeOnWindowResize && !props.disableOverflow) {
                $(window).off('resize', handleOnWindowResize);
            }

            const dialog = contentRef.current;

            if (dialog == null) {
                return;
            }

            if (resizeIsAllowed) {
                $(dialog).resizable('destroy');
            }

            if (moveIsAllowed) {
                $(dialog).draggable('destroy');
            }
        };
    }, [resizable, draggable, dialogName, closeOnWindowResize]);

    return {
        containerSize: containerSize,
        initialContainerHeight,
        initialContentWidth: initialContentWidth.current,
        position
    };
}
